import _ from 'lodash'
import { BrowserType } from '@/@types/ui/BrowserType'
import { getYear } from 'date-fns'
import { ApiError } from '@/@types/generated'

export type ErrorResponse = {
  message: string | any[]
  code: string | number
  requestId?: string
}

export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)

  if (_.size(parts) > 0) {
    return parts.pop()?.split(';').shift()
  }
}

export const openExternalLink = (link: string) => {
  const win = window.open(link, '_blank', 'noopener noreferrer')
  if (win) {
    win.focus()
  }
}

export const getBrowserType = (): BrowserType | null => {
  const userAgent = navigator.userAgent

  switch (userAgent) {
    case userAgent.match(/Edge/i)?.input:
      return BrowserType.Edge
    case userAgent.match(/Chrome/i)?.input:
      return BrowserType.Chrome
    case userAgent.match(/Firefox/i)?.input:
      return BrowserType.Firefox
    case userAgent.match(/Trident/i)?.input:
      return BrowserType.IE
    case userAgent.match(/Safari/i)?.input:
      return BrowserType.Safari

    default:
      return null
  }
}

export const supportedBrowser = (): boolean =>
  _.indexOf(
    [
      BrowserType.Chrome,
      BrowserType.Edge,
      BrowserType.Firefox,
      BrowserType.Safari,
    ],
    getBrowserType()
  ) !== -1

export const unsupportedBrowser = (): boolean =>
  _.indexOf([BrowserType.IE], getBrowserType()) !== -1

export const parseErrorBlob = async (error: any): Promise<ErrorResponse> => {
  const newError = error
  if (newError?.response?.data instanceof Blob) {
    const text = await newError.response.data.text()
    try {
      newError.response.data = JSON.parse(text)
    } catch {
      // Not JSON (e.g. plain-text error body) — fall back to the raw text as the message.
      newError.response.data = { message: text }
    }
  }

  return getErrorMessage(newError)
}

/**
 * Normalizes ASP.NET `ValidationProblemDetails` from either client transport
 * into a `{ field: messages[] }` map. Handles:
 *  - the axios shape (`error.response.data.errors`)
 *  - the generated `ApiError` shape (`error.body.errors`)
 *  - a flat array of messages, mapped to the unkeyed (`""`) field
 *
 * Reusable across the app, not password-specific.
 */
export const getValidationErrors = (error: any): Record<string, string[]> => {
  const raw =
    error?.response?.data?.errors ??
    (error instanceof ApiError ? error.body?.errors : undefined) ??
    error?.body?.errors ??
    error?.errors

  if (!raw) return {}

  if (Array.isArray(raw)) {
    return { '': raw.map((m) => String(m)) }
  }

  const result: Record<string, string[]> = {}
  for (const [key, value] of Object.entries(raw)) {
    result[key] = Array.isArray(value)
      ? value.map((m) => String(m))
      : [String(value)]
  }
  return result
}

export const getErrorMessage = (error: any): ErrorResponse => {
  let message
  const code = error?.response?.status ?? error?.status ?? error?.code
  const requestId =
    error?.response?.data?.requestId ??
    error?.response?.requestId ??
    error?.response?.headers['x-request-id'] ??
    ''

  if (code === 426) {
    message = error.message ? error.message : error.response.data.message
  } else if (
    typeof error.response?.data === 'string' &&
    error.response.data.trim() !== ''
  ) {
    // Some endpoints (e.g. UnprocessableEntity(result.Error.Message)) return the error
    // message as a bare JSON string rather than an object with .detail/.message — those don't
    // match any of the object-shaped checks below and would otherwise fall through to the
    // generic axios "Request failed with status code ..." message.
    message = error.response.data
  } else if (error.response?.data?.detail) {
    message = error.response.data.detail
  } else if (error.response?.data?.message) {
    message = error.response.data.message
  } else if (_.size(getValidationErrors(error)) > 0) {
    // Must be checked before the generic ApiError branch below — an ApiError with a
    // ValidationProblemDetails body (`.body.errors`) is exactly the case getValidationErrors
    // exists to handle, but the ApiError branch doesn't know about `.errors` (plural) at all
    // and would otherwise swallow these messages behind a generic fallback.
    // Plain strings, not JSX — showError shows each entry as its own toast (one per
    // validation message) and stringifies non-string array items, which mangles JSX into
    // "[object Object]" instead of rendering it.
    const validationErrors = getValidationErrors(error)
    message = _.flatMap(validationErrors, (messages) => messages)

    if (requestId) {
      message.push(`Reference Id: ${requestId}`)
    }

    return { message, code, requestId }
  } else if (error instanceof ApiError) {
    if (typeof error.body === 'string' && error.body.trim() !== '')
      message = error.body
    else if (typeof error.body?.error === 'string') message = error.body.error
    else if (typeof error.body?.error?.message === 'string')
      message = error.body.error.message
    else if (typeof error.body?.detail === 'string') message = error.body.detail
    else message = error.message
  } else if (error.response?.statusText) {
    message = error.response.statusText
  } else {
    message = error.message ? error.message : error
  }

  if (requestId) {
    message += `\nReference Id: ${requestId}`
  }

  return { message, code, requestId }
}

export const getRandomInt = (min: number = 0, max: number = 1000): number => {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export const buildCsv = (
  incomingData: any,
  headers: CsvHeader[],
  labels?: any[]
): Blob => {
  const rows = _.map(incomingData, (data) =>
    _.map(headers, (hData) => {
      const value = _.get(data, hData.key)

      let label = _.find(labels, (s) => s.id === value)?.name
      if (_.indexOf(['tasks', 'milestones'], hData.key) !== -1) {
        label = _.find(labels, (s) => s.id === _.first(value))
      }

      if (_.isObject(value) && !_.isArray(value))
        return `"${JSON.stringify(value).replace(/"/g, '""')}"`

      switch (hData.key) {
        case 'ideaRankingId':
        case 'projectStatusId':
          return label
        case 'tasks':
          return label ? `${label?.taskName} | ${label?.taskNumber}` : ''
        case 'milestones':
          return label
            ? `${label?.milestoneName} | ${label?.milestoneNumber}`
            : ''
        case 'modifiedDate':
          return value?.date('MM/dd/yyyy HH:mm:ss')
        case 'isNew':
          return value ? 'Created' : 'Modified'
        default:
          return value
      }
    })
  )
  const csv = [_.map(headers, (h) => h.header).join(','), ...rows].join('\n')

  return new Blob([`${csv}`], { type: 'text/csv;charset=utf-8' })
}

export type CsvHeader = { key: string; header: string }

export const convertToBase64 = async (file?: File): Promise<any> => {
  if (!file) return null

  try {
    const reader = new FileReader()

    return await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
      reader.readAsDataURL(file)
    })
  } catch (e) {
    console.error('There was an error converting File to base64', e)
  }
}

export const validateUsPhoneNumber = (phoneNumber: string): boolean => {
  const cleaned = phoneNumber.replace(/\D/g, '')
  const validFormat = /^(1\d{10}|\d{10})$/

  return validFormat.test(cleaned)
}

export const getYearRange = (offset = 10): number[] => {
  const currentYear = getYear(new Date())
  const startYear = currentYear - offset
  const endYear = currentYear + offset

  return Array.from(
    { length: endYear - startYear + 1 },
    (_, index) => startYear + index
  )
}

export const camelToTitleCase = (
  camelCaseString: string | null | undefined
) => {
  const words = (camelCaseString ?? '')
    .split(/(?=[A-Z])/)
    .map((word) => _.capitalize(word))
  return words.join(' ')
}

export const snakeToTitleCase = (
  snakeCaseString: string | null | undefined
) => {
  const words = (snakeCaseString ?? '')
    .split(/(?=_)/)
    .map((word) => _.capitalize(_.replace(word, '_', '')))
  return words.join(' ')
}
